import React, {
  memo,
  useCallback,
  useRef,
  useState,
  useMemo,
  useEffect,
} from "react";
import { useSelector, useDispatch } from "react-redux";

import { fetchPostStation, getOperatorStations } from "Slices/operatorStation";
import { getIsStopsByCurrentSemanticMapEmpty } from "Slices/stops";
import { useOnClickOutside } from "Utils/hooks";

import { ReactComponent as SelectIcon } from "Components/common/assets/selectIconBold.svg";

import {
  HeadingStyled,
  StationSelectionWrapperStyled,
  StationSelectorDropdownStyled,
  CurrentItemStyled,
  DropdownContentStyled,
  DropdownItemStyled,
} from "./styles";

const DROPDOWN_ID = "station-picker-dropdown";
const DROPDOWN_LIST_ID = "station-picker-list";

type StationPickerProps = {
  customStyles?: {
    HeadingStyled?: typeof HeadingStyled;
    StationSelectionWrapperStyled?: typeof StationSelectionWrapperStyled;
    StationSelectorDropdownStyled?: typeof StationSelectorDropdownStyled;
    CurrentItemStyled?: typeof CurrentItemStyled;
    DropdownContentStyled?: typeof DropdownContentStyled;
    DropdownItemStyled?: typeof DropdownItemStyled;
  };
  enableKeyboardNavigation?: boolean;
};

const StationPicker = memo((props: StationPickerProps = {}) => {
  const { customStyles, enableKeyboardNavigation = false } = props;
  const dispatch = useDispatch();
  const stations = useSelector(getOperatorStations);
  const isStopsEmpty = useSelector(getIsStopsByCurrentSemanticMapEmpty);

  const ref = useRef() as React.MutableRefObject<HTMLDivElement>;
  const buttonRef = useRef<HTMLDivElement>(null);
  const [showDropdown, setShowDropdown] = useState(false);
  const [focusedIndex, setFocusedIndex] = useState<number>(-1);

  const noStationsAvailable = useMemo(() => {
    return isStopsEmpty;
  }, [isStopsEmpty]);

  // Sort stations alphabetically by name
  const sortedStations = useMemo(() => {
    return [...stations].sort((a, b) =>
      (a.name || "").localeCompare(b.name || "", undefined, {
        sensitivity: "base",
      })
    );
  }, [stations]);

  const handleClickOutside = useCallback(() => {
    setShowDropdown(false);
    setFocusedIndex(-1);
  }, []);
  useOnClickOutside(ref, handleClickOutside);

  const { selectedStation, selectedStationId } = useMemo(() => {
    const station = stations.find((each) => each.isPicked);
    return {
      selectedStationId: station?.id,
      selectedStation: station?.name,
    };
  }, [stations]);

  // Reset focused index when dropdown closes or stations change
  useEffect(() => {
    if (!showDropdown) {
      setFocusedIndex(-1);
    }
  }, [showDropdown]);

  // Find the index of the selected station in sorted stations
  const selectedIndex = useMemo(() => {
    if (!selectedStationId) return -1;
    return sortedStations.findIndex(
      (station) => station.id === selectedStationId
    );
  }, [selectedStationId, sortedStations]);

  // Set initial focus to selected station when dropdown opens
  useEffect(() => {
    if (showDropdown && selectedIndex >= 0 && enableKeyboardNavigation) {
      setFocusedIndex(selectedIndex);
    }
  }, [showDropdown, selectedIndex, enableKeyboardNavigation]);

  const handleToggleDropdown = useCallback(() => {
    if (noStationsAvailable) return;
    const newState = !showDropdown;
    setShowDropdown(newState);
    if (!newState) {
      setFocusedIndex(-1);
    }
  }, [showDropdown, noStationsAvailable]);

  const handleSelectStation = useCallback(
    (stationId: string) => {
      if (stationId !== selectedStationId) {
        dispatch(fetchPostStation({ stopId: stationId }));
      }
      setShowDropdown(false);
      setFocusedIndex(-1);
      // Return focus to button after selection
      if (enableKeyboardNavigation) {
        setTimeout(() => {
          buttonRef.current?.focus();
        }, 0);
      }
    },
    [selectedStationId, dispatch, enableKeyboardNavigation]
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (!enableKeyboardNavigation || noStationsAvailable) return;

      // Handle Enter or Space key
      if (e.key === "Enter" || e.key === " " || e.code === "Space") {
        e.preventDefault();
        if (!showDropdown) {
          setShowDropdown(true);
        } else if (focusedIndex >= 0 && sortedStations[focusedIndex]) {
          handleSelectStation(sortedStations[focusedIndex].id ?? "");
        }
        return;
      }

      switch (e.key) {
        case "Escape":
          e.preventDefault();
          setShowDropdown(false);
          setFocusedIndex(-1);
          buttonRef.current?.focus();
          break;

        case "ArrowDown":
          e.preventDefault();
          if (!showDropdown) {
            setShowDropdown(true);
          } else {
            setFocusedIndex((prev) =>
              prev < sortedStations.length - 1 ? prev + 1 : 0
            );
          }
          break;

        case "ArrowUp":
          e.preventDefault();
          if (showDropdown) {
            setFocusedIndex((prev) =>
              prev > 0 ? prev - 1 : sortedStations.length - 1
            );
          }
          break;

        case "Home":
          e.preventDefault();
          if (showDropdown) {
            setFocusedIndex(0);
          }
          break;

        case "End":
          e.preventDefault();
          if (showDropdown) {
            setFocusedIndex(sortedStations.length - 1);
          }
          break;

        case "Tab":
          // Allow default Tab behavior to move focus away
          setShowDropdown(false);
          setFocusedIndex(-1);
          break;

        default:
          // For other keys, open dropdown if not already open
          if (!showDropdown && e.key.length === 1) {
            // Single character keys - could implement type-ahead search here
            setShowDropdown(true);
          }
          break;
      }
    },
    [
      showDropdown,
      focusedIndex,
      sortedStations,
      noStationsAvailable,
      handleSelectStation,
      enableKeyboardNavigation,
    ]
  );

  // Scroll focused item into view
  useEffect(() => {
    if (enableKeyboardNavigation && showDropdown && focusedIndex >= 0) {
      const itemId = `station-option-${sortedStations[focusedIndex]?.id}`;
      const element = document.getElementById(itemId);
      element?.scrollIntoView({ block: "nearest", behavior: "smooth" });
    }
  }, [focusedIndex, showDropdown, sortedStations, enableKeyboardNavigation]);

  const getActiveDescendantId = useMemo(() => {
    if (
      enableKeyboardNavigation &&
      showDropdown &&
      focusedIndex >= 0 &&
      sortedStations[focusedIndex]
    ) {
      return `station-option-${sortedStations[focusedIndex].id}`;
    }
    return undefined;
  }, [showDropdown, focusedIndex, sortedStations, enableKeyboardNavigation]);

  // Use custom styles if provided, otherwise use defaults
  const Heading = customStyles?.HeadingStyled || HeadingStyled;
  const Wrapper =
    customStyles?.StationSelectionWrapperStyled ||
    StationSelectionWrapperStyled;
  const Dropdown =
    customStyles?.StationSelectorDropdownStyled ||
    StationSelectorDropdownStyled;
  const CurrentItem = customStyles?.CurrentItemStyled || CurrentItemStyled;
  const DropdownContent =
    customStyles?.DropdownContentStyled || DropdownContentStyled;
  const DropdownItem = customStyles?.DropdownItemStyled || DropdownItemStyled;

  return (
    <>
      {!selectedStation && <Heading>no stations selected</Heading>}
      <Wrapper
        hasSelection={!!selectedStationId}
        isDisabled={noStationsAvailable}
        ref={ref}
      >
        <Dropdown
          ref={enableKeyboardNavigation ? buttonRef : undefined}
          onClick={handleToggleDropdown}
          onKeyDown={enableKeyboardNavigation ? handleKeyDown : undefined}
          active={showDropdown}
          role={enableKeyboardNavigation ? "combobox" : undefined}
          aria-expanded={enableKeyboardNavigation ? showDropdown : undefined}
          aria-haspopup={enableKeyboardNavigation ? "listbox" : undefined}
          aria-controls={
            enableKeyboardNavigation && showDropdown
              ? DROPDOWN_LIST_ID
              : undefined
          }
          aria-activedescendant={getActiveDescendantId}
          aria-label={enableKeyboardNavigation ? "Select a station" : undefined}
          tabIndex={
            enableKeyboardNavigation
              ? noStationsAvailable
                ? -1
                : 0
              : undefined
          }
          id={enableKeyboardNavigation ? DROPDOWN_ID : undefined}
        >
          <CurrentItem hasSelection={!!selectedStation}>
            {selectedStation
              ? selectedStation
              : noStationsAvailable
              ? "No stations found"
              : "Select a station >"}
          </CurrentItem>
          {!noStationsAvailable && selectedStation && <SelectIcon />}
        </Dropdown>
        {!noStationsAvailable && showDropdown && (
          <DropdownContent
            role={enableKeyboardNavigation ? "listbox" : undefined}
            id={enableKeyboardNavigation ? DROPDOWN_LIST_ID : undefined}
            aria-labelledby={enableKeyboardNavigation ? DROPDOWN_ID : undefined}
          >
            {sortedStations.map((station, index) => {
              const { id, name } = station;
              const isSelected = id === selectedStationId;
              const isFocused =
                enableKeyboardNavigation && index === focusedIndex;
              const itemId = enableKeyboardNavigation
                ? `station-option-${id}`
                : undefined;

              return (
                <DropdownItem
                  key={id}
                  id={itemId}
                  role={enableKeyboardNavigation ? "option" : undefined}
                  aria-selected={
                    enableKeyboardNavigation ? isSelected : undefined
                  }
                  onClick={() => handleSelectStation(id ?? "")}
                  onMouseEnter={
                    enableKeyboardNavigation
                      ? () => setFocusedIndex(index)
                      : undefined
                  }
                  data-focused={isFocused}
                  tabIndex={enableKeyboardNavigation ? -1 : undefined}
                >
                  {name}
                </DropdownItem>
              );
            })}
          </DropdownContent>
        )}
      </Wrapper>
    </>
  );
});

StationPicker.displayName = "StationPicker";

export default StationPicker;
